import { counting } from 'radash';
import { deepClone } from 'valtio/utils';

import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import { ComponentInstanceService } from '@diagram/services/ComponentInstanceService';
import { ConnectionService } from '@diagram/services/ConnectionService';
import { DiagramService } from '@diagram/services/DiagramService';

import { DiagramComponentInstance, DiagramConnection } from 'models';

const DiagramSnippetHelpers = {
    swr: {
        list: '/diagramSnippets',
    },

    generateName: (componentInstances: DiagramComponentInstance[]) => {
        const typeCounts = counting(componentInstances, (ci) => ci.componentType);

        const parts = Object.entries(typeCounts).map(([type, count]) => {
            const definition = ComponentInstanceHelpers.definition(type as DiagramComponentInstance['componentType']);

            return count > 1 ? `${count}x ${definition.plural}` : definition.name;
        });

        return parts.join(', ');
    },

    prepareSnippetData: (componentInstanceIds: string[]) => {
        const diagram = DiagramService.get();

        const componentInstances = ComponentInstanceService.getByIds(componentInstanceIds);
        const internalConnections = ConnectionService.getInternalConnectionsForComponentInstances(
            componentInstanceIds,
            diagram,
        );

        const clonedInstances: Record<string, DiagramComponentInstance> = {};
        const clonedConnections: Record<string, DiagramConnection> = {};

        const minX = Math.min(...componentInstances.map((ci) => ci.position.x));
        const minY = Math.min(...componentInstances.map((ci) => ci.position.y));

        componentInstances.forEach((ci) => {
            const clone: DiagramComponentInstance = deepClone(ci);

            clone.designator = '';
            clone.indicator = 0;

            clone.position = {
                x: clone.position.x - minX,
                y: clone.position.y - minY,
            };

            clonedInstances[ci.id] = clone;
        });

        internalConnections.forEach((connection) => {
            clonedConnections[connection.id] = deepClone(connection);
        });

        const name = DiagramSnippetHelpers.generateName(componentInstances);

        return {
            name,
            componentInstances: clonedInstances,
            connections: clonedConnections,
        };
    },
};

export { DiagramSnippetHelpers };
